import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FilterListIcon from "@mui/icons-material/FilterList";
import { toast } from "react-toastify";
import { getEventPhotos, formatApiDate, locationFilterParams } from "../../api/superAdminApi";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import SuperAdminPageHeader from "./SuperAdminPageHeader";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export default function VMSuperAdminEventPhotos() {
  const { adminScope, locationLocked } = useAdminPanel();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const loc = useLocationCascading();

  const buildFilters = useCallback(
    (pageOverride) => {
      const params = {
        page: pageOverride !== undefined ? pageOverride : page,
        no_of_rows: rowsPerPage,
        ...locationFilterParams(loc, adminScope, { locked: locationLocked }),
      };
      const from = formatApiDate(fromDate);
      const to = formatApiDate(toDate);
      if (from) params.from = from;
      if (to) params.to = to;
      if (searchKey.trim()) params.search = searchKey.trim();
      return params;
    },
    [page, rowsPerPage, fromDate, toDate, searchKey, loc, adminScope, locationLocked]
  );

  const loadData = useCallback(
    async (pageOverride) => {
      setLoading(true);
      try {
        const res = await getEventPhotos(buildFilters(pageOverride));
        setRows(res.data?.data || []);
        setTotalRecords(res.data?.totalCount ?? 0);
      } catch {
        setRows([]);
        setTotalRecords(0);
        toast.info("Event photos API unavailable.");
      } finally {
        setLoading(false);
      }
    },
    [buildFilters]
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <SuperAdminPageHeader
        title="Event Photos"
        actions={
          <Button variant={filtersOpen ? "contained" : "outlined"} startIcon={<FilterListIcon />} onClick={() => setFiltersOpen((o) => !o)}>
            Filters
          </Button>
        }
      />

      {filtersOpen ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker label="From Date" value={fromDate} onChange={setFromDate} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker label="To Date" value={toDate} onChange={setToDate} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", fullWidth: true } }} />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={2}>
              <SuperAdminLocationSelects {...loc} locationLocked={locationLocked} lockedScope={adminScope} itemProps={{ xs: 12, sm: 6, md: 4 }} />
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField fullWidth size="small" label="Search" value={searchKey} onChange={(e) => setSearchKey(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="contained" onClick={() => { setPage(0); loadData(0); }} disabled={loading}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {["Enquiry ID", "Customer", "Venue", "City", "Status", "Photos", "Preview"].map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} aria-label="Loading event photos" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No event photo albums found.</TableCell></TableRow>
            ) : (
              rows.map((row, idx) => {
                const urls = Array.isArray(row.image_urls) ? row.image_urls : [];
                return (
                  <TableRow key={row.enquiry_id ?? idx}>
                    <TableCell>{row.enquiry_id ?? "—"}</TableCell>
                    <TableCell>{row.customer_name ?? "—"}</TableCell>
                    <TableCell>{row.venue_name ?? "—"}</TableCell>
                    <TableCell>{row.city_name ?? "—"}</TableCell>
                    <TableCell>{row.enquiry_status ?? "—"}</TableCell>
                    <TableCell>{row.photo_count ?? urls.length}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {urls.slice(0, 3).map((url) => (
                          <Avatar key={url} src={url} variant="rounded" sx={{ width: 36, height: 36 }} />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS} count={totalRecords} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, p) => { setPage(p); loadData(p); }} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); loadData(0); }} colSpan={7} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}
